<template>
   <div class="lookup-result">
      <!-- Company header -->
      <div class="lookup-result__header">
         <div>
            <h3 class="lookup-result__company-name">{{ result.companyName }}</h3>
            <a
               v-if="result.linkedinUrl"
               :href="result.linkedinUrl"
               target="_blank"
               rel="noopener noreferrer"
               class="lookup-result__linkedin"
            >
               LinkedIn Profile
            </a>
         </div>
         <div class="lookup-result__score-badge" :class="scoreBadgeClass">
            <span class="lookup-result__score-number">{{ result.qualificationScore }}</span>
            <span class="lookup-result__score-denom">/10</span>
         </div>
      </div>

      <!-- Employee count -->
      <p v-if="result.approximateEmployeeCount" class="lookup-result__employees">
         <strong>Approximate size:</strong> {{ result.approximateEmployeeCount }}
      </p>

      <!-- Score label -->
      <b-alert show :variant="scoreVariant" class="lookup-result__signal-label">
         {{ scoreLabel }}
      </b-alert>

      <!-- Explanation -->
      <div class="lookup-result__explanation" v-html="toHtml(result.scoreExplanation)"></div>

      <!-- Technical roles -->
      <div v-if="result.technicalRoles && result.technicalRoles.length" class="mb-4">
         <h5 class="lookup-result__section-title">US Technical Roles</h5>
         <div class="lookup-result__roles">
            <a
               v-for="role in result.technicalRoles"
               :key="role"
               :href="linkedinRoleUrl(role)"
               target="_blank"
               rel="noopener noreferrer"
               class="lookup-result__role-link"
            >
               <b-badge variant="info" class="mr-1 mb-1">{{ role }}</b-badge>
            </a>
         </div>
      </div>

      <!-- Public R&D evidence -->
      <div v-if="result.publicRdEvidence && result.publicRdEvidence.length" class="mb-4">
         <h5 class="lookup-result__section-title">Public R&amp;D Evidence</h5>
         <ul class="lookup-result__evidence-list">
            <li
               v-for="(item, i) in result.publicRdEvidence"
               :key="i"
               class="lookup-result__evidence-item"
            >
               <a :href="item.url" target="_blank" rel="noopener noreferrer">{{ item.url }}</a>
               <p class="lookup-result__evidence-desc">{{ item.description }}</p>
            </li>
         </ul>
      </div>

      <!-- Discovery questions -->
      <div
         v-if="result.discoveryQuestions && result.discoveryQuestions.length"
         class="lookup-result__questions-box"
      >
         <h5 class="lookup-result__section-title">Questions To Ask</h5>
         <p class="lookup-result__questions-intro">
            These questions may help the prospect realize work they're doing that may qualify:
         </p>
         <ol class="lookup-result__questions-list">
            <li
               v-for="(q, i) in result.discoveryQuestions"
               :key="i"
               class="lookup-result__question-item"
            >
               {{ q }}
            </li>
         </ol>
      </div>
   </div>
</template>

<script>
import {prospectScoreTier} from '@/helpers/utils';

export default {
   name: 'ProspectLookupResult',

   props: {
      result: {
         type: Object,
         required: true,
      },
   },

   computed: {
      scoreTier() {
         return prospectScoreTier(this.result.qualificationScore);
      },

      scoreVariant() {
         return {high: 'success', mid: 'warning', low: 'info'}[this.scoreTier];
      },

      scoreBadgeClass() {
         return `lookup-result__score-badge--${this.scoreTier}`;
      },

      scoreLabel() {
         return {
            high: 'Strong Qualification Signals',
            mid: 'Some Qualification Signals',
            low: 'Limited Public Signals',
         }[this.scoreTier];
      },
   },

   methods: {
      toHtml(text) {
         if (!text) return '';
         let s = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
         s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

         const lines = s.split('\n');
         const out = [];
         let listTag = null;

         const closeList = () => {
            if (listTag) {
               out.push(`</${listTag}>`);
               listTag = null;
            }
         };

         for (const raw of lines) {
            const line = raw.trim();
            const orderedMatch = line.match(/^\d+\.\s+(.*)/);
            const bulletMatch = line.match(/^[-*]\s+(.*)/);

            if (orderedMatch) {
               if (listTag !== 'ol') {
                  closeList();
                  out.push('<ol>');
                  listTag = 'ol';
               }
               out.push(`<li>${orderedMatch[1]}</li>`);
            } else if (bulletMatch) {
               if (listTag !== 'ul') {
                  closeList();
                  out.push('<ul>');
                  listTag = 'ul';
               }
               out.push(`<li>${bulletMatch[1]}</li>`);
            } else {
               closeList();
               if (line) out.push(`<p>${line}</p>`);
            }
         }
         closeList();
         return out.join('');
      },

      linkedinRoleUrl(role) {
         if (this.result.linkedinUrl) {
            const slug = this.result.linkedinUrl.replace(/\/$/, '').split('/').pop();
            return `https://www.linkedin.com/company/${slug}/people/?keywords=${encodeURIComponent(
               role
            )}`;
         }
         const q = encodeURIComponent(`${role} ${this.result.companyName}`);
         return `https://www.linkedin.com/search/results/people/?keywords=${q}`;
      },
   },
};
</script>

<style lang="scss" scoped>
.lookup-result {
   &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.75rem;
   }

   &__company-name {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 0.25rem;
   }

   &__linkedin {
      display: inline-block;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.25rem 0.625rem;
      border: 1px solid $primary;
      border-radius: 4px;
      color: $primary;
      text-decoration: none;
      margin-top: 0.375rem;

      &:hover {
         background-color: $primary;
         color: #fff;
         text-decoration: none;
      }
   }

   &__score-badge {
      display: flex;
      align-items: baseline;
      border-radius: 8px;
      padding: 0.375rem 0.75rem;
      min-width: 4.5rem;
      justify-content: center;

      &--high {
         background-color: rgba($success, 0.15);
      }
      &--mid {
         background-color: rgba($warning, 0.25);
      }
      &--low {
         background-color: $primary-100;
      }
   }

   &__score-number {
      font-size: 1.75rem;
      font-weight: 700;
      line-height: 1;
   }

   &__score-denom {
      font-size: 0.875rem;
      margin-left: 2px;
      opacity: 0.7;
   }

   &__employees {
      font-size: 0.875rem;
      margin-bottom: 0.75rem;
   }

   &__signal-label {
      padding: 0.5rem 0.875rem;
      font-size: 0.875rem;
      margin-bottom: 1rem;
   }

   &__explanation {
      font-size: 0.9375rem;
      margin-bottom: 1.25rem;

      p,
      ol,
      ul {
         margin-bottom: 0.5rem;
      }
      p:last-child,
      ol:last-child,
      ul:last-child {
         margin-bottom: 0;
      }
      ol,
      ul {
         padding-left: 1.25rem;
      }
   }

   &__section-title {
      font-size: 0.875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: $gray-600;
      margin-bottom: 0.5rem;
   }

   &__roles {
      display: flex;
      flex-wrap: wrap;
   }

   &__role-link {
      text-decoration: none;

      &:hover .badge {
         filter: brightness(0.9);
      }
   }

   &__evidence-list {
      padding-left: 1rem;
      margin-bottom: 0;
   }

   &__evidence-item {
      margin-bottom: 0.75rem;

      a {
         font-size: 0.8125rem;
         word-break: break-all;
      }
   }

   &__evidence-desc {
      font-size: 0.875rem;
      color: $gray-600;
      margin: 0.25rem 0 0;
   }

   &__questions-box {
      background-color: $primary-100;
      border-left: 3px solid $primary;
      border-radius: 4px;
      padding: 1rem 1.25rem;
      margin-top: 0.5rem;
   }

   &__questions-intro {
      font-size: 0.875rem;
      color: $gray-600;
      margin-bottom: 0.5rem;
   }

   &__questions-list {
      padding-left: 1.25rem;
      margin-bottom: 0;
   }

   &__question-item {
      font-size: 0.9375rem;
      margin-bottom: 0.5rem;

      &:last-child {
         margin-bottom: 0;
      }
   }
}
</style>
